import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableLoader } from '../../components/Common/Loader';
import { Search, ChevronDown, ArrowLeft, Eye, Edit3, Plus } from 'lucide-react';
import { DistributorStatCards } from './components/DistributorStatCards';
import { DistributorFilters } from './components/DistributorFilters';
import type { FilterState } from './components/DistributorFilters';
import { AddEditDistributorModal } from './components/AddEditDistributorModal';
import type { DistributorRecord } from './components/AddEditDistributorModal';
import { getDistributorsAPI } from '../../api/distributors';
import type { DistributorFiltersPayload } from '../../api/distributors';
import { getZonesAPI, getCitiesAPI, getDeposAPI, getRoutesAPI } from '../../api/locations';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import Pagination from '../../components/Common/Pagination';

export const DistributorsList: React.FC = () => {
  const navigate = useNavigate();

  const [distributors, setDistributors] = useState<DistributorRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [entriesCount, setEntriesCount] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableSearch, setTableSearch] = useState('');

  // Filter toolbar state
  const [filters, setFilters] = useState<FilterState>({
    name: '',
    phone: '',
    zone: '',
    city: '',
    depo: '',
    route: '',
  });

  // Location dropdown options from API
  const [apiZoneOptions, setApiZoneOptions] = useState<string[]>([]);
  const [apiCityOptions, setApiCityOptions] = useState<string[]>([]);
  const [apiDepoOptions, setApiDepoOptions] = useState<string[]>([]);
  const [apiRouteOptions, setApiRouteOptions] = useState<string[]>([]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState<DistributorRecord | null>(null);

  // Fetch location dropdown options from location APIs
  useEffect(() => {
    let isMounted = true;
    const fetchLocationOptions = async () => {
      try {
        const [zonesRes, citiesRes, deposRes, routesRes] = await Promise.all([
          getZonesAPI().catch(() => null),
          getCitiesAPI().catch(() => null),
          getDeposAPI().catch(() => null),
          getRoutesAPI().catch(() => null),
        ]);

        if (!isMounted) return;

        const extractNames = (res: any) => {
          const list = res?.data || res?.zones || res?.cities || res?.depos || res?.routes || (Array.isArray(res) ? res : []);
          if (Array.isArray(list)) {
            return list.map((item: any) => item.name || item.cityName || item.zoneName || item.depoName || item.routeName || item).filter(Boolean);
          }
          return [];
        };

        const fetchedZones = extractNames(zonesRes);
        const fetchedCities = extractNames(citiesRes);
        const fetchedDepos = extractNames(deposRes);
        const fetchedRoutes = extractNames(routesRes);

        if (fetchedZones.length > 0) setApiZoneOptions(Array.from(new Set(fetchedZones)));
        if (fetchedCities.length > 0) setApiCityOptions(Array.from(new Set(fetchedCities)));
        if (fetchedDepos.length > 0) setApiDepoOptions(Array.from(new Set(fetchedDepos)));
        if (fetchedRoutes.length > 0) setApiRouteOptions(Array.from(new Set(fetchedRoutes)));
      } catch (err) {
        console.warn('Error fetching location options:', err);
      }
    };
    fetchLocationOptions();
    return () => { isMounted = false; };
  }, []);

  // POST /api/v1/distributor/list integration
  const fetchDistributors = useCallback(async () => {
    try {
      setLoading(true);

      const payload: DistributorFiltersPayload = {};
      if (filters.name) payload.name = filters.name;
      if (filters.phone) payload.phone = filters.phone;
      if (filters.zone) payload.zoneName = filters.zone;
      if (filters.city) payload.cityName = filters.city;
      if (filters.depo) payload.depo = filters.depo;
      if (filters.route) payload.route = filters.route;
      if (tableSearch) payload.search = tableSearch;

      const res = await getDistributorsAPI(payload);
      const rawList = res?.data || res?.distributors || (Array.isArray(res) ? res : null);

      if (Array.isArray(rawList)) {
        const mapped: DistributorRecord[] = rawList.map((item: any) => {
          const zoneName = item.zoneName || item.zone?.name || item.zone || '';
          const territoryName = item.territoryName || item.territory?.name || item.territory || '';
          const cityName = item.cityName || item.city?.name || item.city || '';

          const zoneCityDisplay = [zoneName, territoryName, cityName]
            .filter(Boolean)
            .join(' → ') || item.zoneCity || 'N/A';

          const isStatusActive =
            item.status === 'ACTIVE' ||
            item.status === 'Active' ||
            item.status === 1 ||
            item.status === true;

          return {
            id: item.id || Date.now(),
            name: item.companyName || item.name || item.distributorName || 'N/A',
            companyName: item.companyName || item.name || item.distributorName || 'N/A',
            sapCode: item.sapCode || item.sapCustomerId || item.code || item.distributorCode || 'N/A',
            phone: item.phone || item.contactNumber || item.mobile || 'N/A',
            zone: zoneName,
            territory: territoryName,
            city: cityName || 'N/A',
            zoneCity: zoneCityDisplay,
            pan: item.pan || item.panNo || item.panNumber || item.pan_no || 'N/A',
            fssai: item.fssai || item.fssaiNo || item.fssaiNumber || item.fssaiLicenceNo || item.fssai_no || 'N/A',
            depo: item.depoName || item.depo?.name || item.depo || 'N/A',
            route: item.routeName || item.route?.name || item.route || 'N/A',
            status: isStatusActive ? 'Active' : 'Inactive',
            createdOn: item.createdAt
              ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : (item.createdOn || 'N/A'),
            email: item.email || '',
            address: item.address || '',
          };
        });
        setDistributors(mapped);
      }
    } catch (err) {
      console.warn('Error fetching distributors list:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, tableSearch]);

  useEffect(() => {
    fetchDistributors();
  }, [fetchDistributors]);

  // Reset page to 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, tableSearch, entriesCount]);

  // Dropdown options (API options merged with derived fallback options)
  const zoneOptions = useMemo(
    () => Array.from(new Set([...apiZoneOptions, ...distributors.map((d) => d.zone).filter((z): z is string => Boolean(z))])),
    [apiZoneOptions, distributors]
  );
  const cityOptions = useMemo(
    () => Array.from(new Set([...apiCityOptions, ...distributors.map((d) => d.city).filter(Boolean)])),
    [apiCityOptions, distributors]
  );
  const depoOptions = useMemo(
    () => Array.from(new Set([...apiDepoOptions, ...distributors.map((d) => d.depo).filter(Boolean)])),
    [apiDepoOptions, distributors]
  );
  const routeOptions = useMemo(
    () => Array.from(new Set([...apiRouteOptions, ...distributors.map((d) => d.route).filter(Boolean)])),
    [apiRouteOptions, distributors]
  );

  // Stats calculation
  const totalCount = distributors.length;
  const activeCount = distributors.filter((d) => d.status === 'Active').length;
  const inactiveCount = distributors.filter((d) => d.status === 'Inactive').length;
  const totalRoutesCount = new Set(distributors.map((d) => d.route)).size;

  // Filter logic
  const filteredDistributors = useMemo(() => {
    return distributors.filter((d) => {
      if (filters.name && !d.name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }
      if (filters.phone && !d.phone.includes(filters.phone)) {
        return false;
      }
      if (filters.zone && d.zone !== filters.zone) {
        return false;
      }
      if (filters.city && d.city !== filters.city) {
        return false;
      }
      if (filters.depo && d.depo !== filters.depo) {
        return false;
      }
      if (filters.route && d.route !== filters.route) {
        return false;
      }
      if (tableSearch) {
        const query = tableSearch.toLowerCase();
        const matchesName = d.name.toLowerCase().includes(query);
        const matchesSap = (d.sapCode || d.code || '').toLowerCase().includes(query);
        const matchesPhone = d.phone.includes(query);
        const matchesCity = d.city.toLowerCase().includes(query);
        const matchesPan = (d.pan || '').toLowerCase().includes(query);
        const matchesFssai = (d.fssai || '').toLowerCase().includes(query);
        const matchesDepo = d.depo.toLowerCase().includes(query);
        const matchesRoute = d.route.toLowerCase().includes(query);
        if (!matchesName && !matchesSap && !matchesPhone && !matchesCity && !matchesPan && !matchesFssai && !matchesDepo && !matchesRoute) {
          return false;
        }
      }
      return true;
    });
  }, [distributors, filters, tableSearch]);

  // Paginated Distributors for Table view
  const paginatedDistributors = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesCount;
    return filteredDistributors.slice(startIndex, startIndex + entriesCount);
  }, [filteredDistributors, currentPage, entriesCount]);

  // Actions
  const _handleOpenAddModal = () => {
    setEditingDistributor(null);
    setIsModalOpen(true);
  };

  const _handleOpenEditModal = (dist: DistributorRecord) => {
    setEditingDistributor(dist);
    setIsModalOpen(true);
  };
  void _handleOpenAddModal;
  void _handleOpenEditModal;

  const handleSaveDistributor = (data: Partial<DistributorRecord>) => {
    if (editingDistributor) {
      setDistributors((prev) =>
        prev.map((d) => (d.id === editingDistributor.id ? { ...d, ...data } as DistributorRecord : d))
      );
    } else {
      setDistributors((prev) => [data as DistributorRecord, ...prev]);
    }
  };

  const handleViewDetails = (dist: DistributorRecord) => {
    navigate('/dashboard/view-distributor', { state: { distributor: dist } });
  };

  const handleEditDistributor = (dist: DistributorRecord) => {
    navigate(`/distributor/edit/${dist.id}`);
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'SAP Code', 'Distributor Name', 'Phone', 'City', 'PAN', 'FSSAI', 'Depo', 'Route'];
    const rows = filteredDistributors.map((d) => [
      d.id,
      d.sapCode || d.code || '',
      d.name,
      d.phone,
      d.city,
      d.pan || '',
      d.fssai || '',
      d.depo,
      d.route,
    ]);
    exportToCSV('distributors_list', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Top Header Row with Title & Red/Yellow Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Distributors
          </h1>
        </div>

        {/* Top Right Action Buttons */}
        <div className="flex items-center gap-3">
          <CSVButton onClick={handleExportCSV} />
          <button
            onClick={() => navigate('/distributor/add')}
            className="px-4 py-2 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Add Distributor</span>
          </button>
        </div>
      </div>

      {/* Filter Card Toolbar */}
      <DistributorFilters
        filters={filters}
        onFilterChange={setFilters}
        onReset={() => setFilters({ name: '', phone: '', zone: '', city: '', depo: '', route: '' })}
        zoneOptions={zoneOptions}
        cityOptions={cityOptions}
        depoOptions={depoOptions}
        routeOptions={routeOptions}
      />

      {/* 4 KPI Stat Cards */}
      <DistributorStatCards
        totalDistributors={totalCount}
        activeDistributors={activeCount}
        inactiveDistributors={inactiveCount}
        totalRoutes={totalRoutesCount}
      />

      {/* Main Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Table Controls Top Header */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white">
          {/* Show entries select */}
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <span>Show</span>
            <div className="relative">
              <select
                value={entriesCount}
                onChange={(e) => {
                  setEntriesCount(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="appearance-none bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 pr-7 text-xs font-bold text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
            </div>
            <span>entries</span>
          </div>

          {/* Quick Search bar */}
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search distributors..."
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>

        {/* Table Canvas */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                <th className="py-3.5 px-6">ID</th>
                <th className="py-3.5 px-6">SAP Code</th>
                <th className="py-3.5 px-6">Distributor Name</th>
                <th className="py-3.5 px-6">Phone No.</th>
                <th className="py-3.5 px-6">City</th>
                <th className="py-3.5 px-6">PAN</th>
                <th className="py-3.5 px-6">FSSAI</th>
                <th className="py-3.5 px-6">Depo</th>
                <th className="py-3.5 px-6">Route</th>
                <th className="py-3.5 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
              {paginatedDistributors.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-400">{item.id}</td>
                  <td className="py-4 px-6 font-semibold text-slate-600">{item.sapCode || item.code || 'N/A'}</td>
                  <td className="py-4 px-6 font-bold text-slate-800">{item.companyName || item.name}</td>
                  <td className="py-4 px-6 font-medium text-slate-600">{item.phone && item.phone !== 'null' ? item.phone : 'N/A'}</td>
                  <td className="py-4 px-6 font-medium text-slate-600">{item.city && item.city !== 'null' ? item.city : 'N/A'}</td>
                  <td className="py-4 px-6 font-semibold text-slate-700">{item.pan && item.pan !== 'null' ? item.pan : 'N/A'}</td>
                  <td className="py-4 px-6 font-medium text-slate-600">{item.fssai && item.fssai !== 'null' ? item.fssai : 'N/A'}</td>
                  <td className="py-4 px-6 font-medium text-slate-600">{item.depo && item.depo !== 'null' ? item.depo : 'N/A'}</td>
                  <td className="py-4 px-6 font-semibold text-slate-700">{item.route && item.route !== 'null' ? item.route : 'N/A'}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => handleViewDetails(item)}
                        className="p-1 text-slate-600 hover:text-[#C1272D] transition-colors cursor-pointer"
                        title="View Details"
                      >
                        <Eye className="w-4.5 h-4.5" />
                      </button>
                      <button
                        onClick={() => handleEditDistributor(item)}
                        className="p-1 text-slate-600 hover:text-[#C1272D] transition-colors cursor-pointer"
                        title="Edit Distributor"
                      >
                        <Edit3 className="w-4.5 h-4.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading ? (
                <TableLoader colSpan={10} text="Loading..." />
              ) : filteredDistributors.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-slate-400 font-medium">
                    No distributors found matching your filter criteria.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {/* Table Footer Pagination */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredDistributors.length}
          entriesLimit={entriesCount}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Add / Edit Modal */}
      <AddEditDistributorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveDistributor}
        initialData={editingDistributor}
      />
    </div>
  );
};

export default DistributorsList;

